import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import ResponsivePaysMore from '../components/ResponsivePaysMore';
import SelectionCard from '../components/SelectionCard';
import { api } from '../api';
import { isUKSeller } from '../utils/sellerRegion';

// iter-26: rebuild per Payment Details Page UI Spec.
//
//   1. Heading "Payment details" (sentence case) + subtitle
//   2. Earnings summary card — "YOUR EARNINGS" label, profile payment +
//      conditional ID badge bonus (only if upsell accepted) + total in
//      money-positive green (#16A34A) with USD/GBP suffix.
//   3. Bank fields — Full name, Bank name, then Routing+Account paired
//      side-by-side (1fr 1fr → 1fr mobile). UK conditional swaps
//      Routing → Sort code (XX-XX-XX, 6 digits), currency to GBP.
//   4. Finish button — orange #F26C1F, 16/32 padding, white check icon,
//      disabled until all required fields valid.
//   5. Payout note "Payment usually arrives within 24-48 hours after
//      approval." 12/#888 below button.
//   NO Trustpilot.

const NUMERIC_RE = /^\d+$/;
const SORT_CODE_RE = /^\d{2}-?\d{2}-?\d{2}$/;
// US ZIP: 5 digits, optionally ZIP+4 (12345 or 12345-6789).
const ZIP_RE = /^\d{5}(-?\d{4})?$/;
// US sellers pick a bank account type so operators route ACH correctly.
const ACCOUNT_TYPES = [
  { value: 'Checking', label: 'Checking' },
  { value: 'Savings', label: 'Savings' },
];

export default function Step21PaymentDetails({ sessionId, sessionData, updateData, onNext }) {
  const isUK = isUKSeller(sessionData);
  const symbol = isUK ? '£' : '$';
  const currency = isUK ? 'GBP' : 'USD';
  const profileAmount = `${symbol}20.00`;
  const bonusAmount = `+${symbol}5.00`;
  // B-IDBADGE-HOLD-PENDING (2026-05-29): the +£5 badge bonus counts toward
  // the payout total ONLY once the badge is actually verified on LinkedIn
  // (verified_badge_present === true) — not on the seller's mere claim.
  // LinkedIn's verified badge is added via an async phone/Persona review
  // that does not surface in-session, so a claimed-but-unverified badge
  // shows a "pending" line and the total stays £20 until the operator
  // confirms it and credits the £5 out-of-band.
  const idBadgeVerified = sessionData?.verifiedBadgePresent === true;
  const idBadgeClaimed = sessionData?.idBadgeAccepted === true
    || sessionData?.idBadgeUpsell === 'Yes'
    || !!sessionData?.idBadgeAddedConfirmedAt;
  // The +£5 is realized only when the seller PURSUED the upsell AND the
  // badge is verified. "verified but never claimed" = the seller already
  // had a badge before the takeover → base £20 (no upsell bonus).
  const idBadgeBonusEarned = idBadgeClaimed && idBadgeVerified;
  const idBadgePending = idBadgeClaimed && !idBadgeVerified;
  const totalNumeric = idBadgeBonusEarned ? 25 : 20;
  const totalAmount = `${symbol}${totalNumeric}.00`;

  const [form, setForm] = useState({
    fullName: sessionData?.paymentFullName || '',
    bankName: sessionData?.paymentBankName || '',
    routing: sessionData?.paymentRouting || '',
    account: sessionData?.paymentAccount || '',
    // US-only fields (migration 146). Persisted to sessionData so a
    // step-back / resume re-populates them.
    accountType: sessionData?.paymentAccountType || '',
    addrLine1: sessionData?.paymentAddressLine1 || '',
    addrLine2: sessionData?.paymentAddressLine2 || '',
    city: sessionData?.paymentCity || '',
    state: sessionData?.paymentState || '',
    zip: sessionData?.paymentZip || '',
  });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // iter-33: per-field validation error messages. Each helper returns
  // an empty string when the field is empty (no nagging) OR valid;
  // otherwise a specific reason. The Finish button stays disabled
  // until every error is empty AND every field is filled — that's the
  // same gate as before, but the seller now sees WHY.
  const fullNameError = (() => {
    const v = form.fullName.trim();
    if (!v) return '';
    if (v.length < 2) return 'Please enter your full name.';
    return '';
  })();

  const bankNameError = (() => {
    const v = form.bankName.trim();
    if (!v) return '';
    if (v.length < 2) return "Please enter your bank's name.";
    return '';
  })();

  const routingError = (() => {
    const v = form.routing.trim();
    if (!v) return '';
    if (isUK) {
      const stripped = v.replace(/\s+/g, '');
      if (!SORT_CODE_RE.test(stripped)) {
        return 'Sort code must be 6 digits (e.g. 12-34-56).';
      }
    } else {
      if (!NUMERIC_RE.test(v)) return 'Routing number must contain digits only, no letters or symbols.';
      if (v.length !== 9) return `Routing number must be exactly 9 digits (you have ${v.length}).`;
    }
    return '';
  })();

  const accountError = (() => {
    const v = form.account.trim();
    if (!v) return '';
    if (!NUMERIC_RE.test(v)) return 'Account number must contain digits only, no letters or symbols.';
    if (isUK) {
      if (v.length !== 8) return `UK account numbers are 8 digits (you have ${v.length}).`;
    } else {
      if (v.length < 4) return `Account number must be at least 4 digits (you have ${v.length}).`;
    }
    return '';
  })();

  const routingValid = !routingError && form.routing.trim().length > 0;
  const accountValid = !accountError && form.account.trim().length > 0;

  // US-only fields (migration 146). These only gate the Finish button
  // for US sellers — UK sellers never see them and are never blocked.
  const zipError = (() => {
    const v = form.zip.trim();
    if (!v) return '';
    if (!ZIP_RE.test(v)) return 'ZIP code must be 5 digits (e.g. 90210).';
    return '';
  })();

  const usFieldsValid = isUK || (
    (form.accountType === 'Checking' || form.accountType === 'Savings')
    && form.addrLine1.trim().length >= 3
    && form.city.trim().length >= 2
    && form.state.trim().length >= 2
    && form.zip.trim().length > 0
    && !zipError
  );

  const canSubmit = form.fullName.trim().length >= 2
    && form.bankName.trim().length >= 2
    && routingValid
    && accountValid
    && usFieldsValid
    && !loading;

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setSubmitError('');
    try {
      // iter-36: BE schema is { bankDetails: BankDetails }. BankDetails
      // wants { bankName, holderName, accountNumber, sortCode? OR
      // routingNumber? }. accountNumber must match ^[A-Z0-9 ]+$ — pure
      // digits pass; we uppercase any letters defensively. Sort code
      // / routing number are XOR per UK / US.
      const accountNumberClean = form.account.trim().replace(/\s+/g, '').toUpperCase();
      const bankDetails = {
        bankName: form.bankName.trim(),
        holderName: form.fullName.trim(),
        accountNumber: accountNumberClean,
      };
      // US sellers (migration 146): send account type + composed mailing
      // address so operators can send ACH / check payments. The address
      // is a single multi-line string the operator reads verbatim.
      let composedAddress = '';
      if (isUK) {
        bankDetails.sortCode = form.routing.trim().replace(/\s+/g, '');
      } else {
        bankDetails.routingNumber = form.routing.trim().replace(/\s+/g, '');
        bankDetails.accountType = form.accountType;
        const addrLines = [form.addrLine1.trim()];
        if (form.addrLine2.trim()) addrLines.push(form.addrLine2.trim());
        addrLines.push(`${form.city.trim()}, ${form.state.trim().toUpperCase()} ${form.zip.trim()}`);
        composedAddress = addrLines.join('\n');
        bankDetails.address = composedAddress;
      }
      await api.updatePayment(sessionId, { bankDetails });
      updateData({
        paymentFullName: form.fullName.trim(),
        paymentBankName: form.bankName.trim(),
        paymentRouting: form.routing.trim(),
        paymentAccount: form.account.trim(),
        paymentCurrency: currency,
        ...(isUK ? {} : {
          paymentAccountType: form.accountType,
          paymentAddressLine1: form.addrLine1.trim(),
          paymentAddressLine2: form.addrLine2.trim(),
          paymentCity: form.city.trim(),
          paymentState: form.state.trim().toUpperCase(),
          paymentZip: form.zip.trim(),
          paymentAddress: composedAddress,
        }),
      });
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save payment details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepLayout title="Payment details" hideTrustpilot>
      <p style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 16,
        lineHeight: 1.55,
        color: '#444',
        margin: '0 0 32px',
      }}>
        Enter your bank details so we can send your earnings.
      </p>

      {/* Earnings summary card */}
      <section
        className="earnings-card"
        role="region"
        aria-labelledby="earnings-card-heading"
      >
        <div id="earnings-card-heading" className="earnings-card__label">YOUR EARNINGS</div>
        <div className="earnings-card__row">
          <span className="earnings-card__name">Profile payment</span>
          <span className="earnings-card__amount">{profileAmount}</span>
        </div>
        {idBadgeBonusEarned && (
          <div className="earnings-card__row">
            <span className="earnings-card__name">ID badge bonus</span>
            <span className="earnings-card__bonus">{bonusAmount}</span>
          </div>
        )}
        {idBadgePending && (
          <div className="earnings-card__row" style={{ opacity: 0.7 }}>
            <span className="earnings-card__name">
              ID badge bonus
              <span style={{ display: 'block', fontSize: 12, color: '#888' }}>
                Added once we verify your badge on LinkedIn
              </span>
            </span>
            <span className="earnings-card__bonus">{bonusAmount}</span>
          </div>
        )}
        <div className="earnings-card__total">
          <span className="earnings-card__total-label">Total</span>
          <span className="earnings-card__total-value">
            {totalAmount}
            <span className="earnings-card__currency">{currency}</span>
          </span>
        </div>
      </section>

      <ResponsivePaysMore placement="paymentDetails" sessionData={sessionData} />

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-field" style={{ maxWidth: 480 }}>
          <label htmlFor="paymentFullName" className="form-field__label">
            Full name <span className="form-field__req">*</span>
          </label>
          <input
            id="paymentFullName"
            type="text"
            value={form.fullName}
            onChange={(e) => update('fullName', e.target.value)}
            className={`form-field__input payment-input${fullNameError ? ' payment-input--error' : ''}`}
            autoComplete="name"
            aria-required="true"
            aria-invalid={fullNameError ? 'true' : 'false'}
          />
          {fullNameError && <p className="form-field__error">{fullNameError}</p>}
        </div>

        <div className="form-field" style={{ maxWidth: 480, marginTop: 20 }}>
          <label htmlFor="paymentBankName" className="form-field__label">
            Bank name <span className="form-field__req">*</span>
          </label>
          <input
            id="paymentBankName"
            type="text"
            value={form.bankName}
            onChange={(e) => update('bankName', e.target.value)}
            className={`form-field__input payment-input${bankNameError ? ' payment-input--error' : ''}`}
            aria-required="true"
            aria-invalid={bankNameError ? 'true' : 'false'}
          />
          {bankNameError && <p className="form-field__error">{bankNameError}</p>}
        </div>

        <div className="payment-pair">
          <div className="form-field">
            <label htmlFor="paymentRouting" className="form-field__label">
              {isUK ? 'Sort code' : 'Routing number'} <span className="form-field__req">*</span>
            </label>
            <input
              id="paymentRouting"
              type="text"
              inputMode="numeric"
              placeholder={isUK ? '6 digits' : '9 digits'}
              value={form.routing}
              onChange={(e) => update('routing', e.target.value)}
              className={`form-field__input payment-input${routingError ? ' payment-input--error' : ''}`}
              aria-required="true"
              aria-invalid={routingError ? 'true' : 'false'}
            />
            {routingError && <p className="form-field__error">{routingError}</p>}
          </div>
          <div className="form-field">
            <label htmlFor="paymentAccount" className="form-field__label">
              Account number <span className="form-field__req">*</span>
            </label>
            <input
              id="paymentAccount"
              type="text"
              inputMode="numeric"
              value={form.account}
              onChange={(e) => update('account', e.target.value)}
              className={`form-field__input payment-input${accountError ? ' payment-input--error' : ''}`}
              aria-required="true"
              aria-invalid={accountError ? 'true' : 'false'}
            />
            {accountError && <p className="form-field__error">{accountError}</p>}
          </div>
        </div>

        {/* US-seller fields (migration 146): bank account type + mailing
            address. Gated to non-UK sellers — the operator needs both to
            send the payment. UK sellers never see these. */}
        {!isUK && (
          <>
            <div className="form-field" style={{ maxWidth: 480, marginTop: 24 }}>
              <label className="form-field__label" style={{ marginBottom: 10, display: 'block' }}>
                Account type <span className="form-field__req">*</span>
              </label>
              <div
                role="radiogroup"
                aria-label="Account type"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                {ACCOUNT_TYPES.map((opt) => (
                  <SelectionCard
                    key={opt.value}
                    id={`accountType-${opt.value}`}
                    name="accountType"
                    value={opt.value}
                    label={opt.label}
                    selected={form.accountType === opt.value}
                    onSelect={(v) => update('accountType', v)}
                    disabled={loading}
                    compact
                  />
                ))}
              </div>
            </div>

            <div className="form-field" style={{ maxWidth: 480, marginTop: 20 }}>
              <label htmlFor="paymentAddrLine1" className="form-field__label">
                Address line 1 <span className="form-field__req">*</span>
              </label>
              <input
                id="paymentAddrLine1"
                type="text"
                value={form.addrLine1}
                onChange={(e) => update('addrLine1', e.target.value)}
                className="form-field__input payment-input"
                autoComplete="address-line1"
                aria-required="true"
                placeholder="Street address"
              />
            </div>

            <div className="form-field" style={{ maxWidth: 480, marginTop: 20 }}>
              <label htmlFor="paymentAddrLine2" className="form-field__label">
                Address line 2 <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                id="paymentAddrLine2"
                type="text"
                value={form.addrLine2}
                onChange={(e) => update('addrLine2', e.target.value)}
                className="form-field__input payment-input"
                autoComplete="address-line2"
                placeholder="Apartment, suite, unit (optional)"
              />
            </div>

            <div className="form-field" style={{ maxWidth: 480, marginTop: 20 }}>
              <label htmlFor="paymentCity" className="form-field__label">
                City <span className="form-field__req">*</span>
              </label>
              <input
                id="paymentCity"
                type="text"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
                className="form-field__input payment-input"
                autoComplete="address-level2"
                aria-required="true"
              />
            </div>

            <div className="payment-pair">
              <div className="form-field">
                <label htmlFor="paymentState" className="form-field__label">
                  State <span className="form-field__req">*</span>
                </label>
                <input
                  id="paymentState"
                  type="text"
                  value={form.state}
                  onChange={(e) => update('state', e.target.value)}
                  className="form-field__input payment-input"
                  autoComplete="address-level1"
                  aria-required="true"
                  placeholder="e.g. CA"
                />
              </div>
              <div className="form-field">
                <label htmlFor="paymentZip" className="form-field__label">
                  ZIP code <span className="form-field__req">*</span>
                </label>
                <input
                  id="paymentZip"
                  type="text"
                  inputMode="numeric"
                  value={form.zip}
                  onChange={(e) => update('zip', e.target.value)}
                  className={`form-field__input payment-input${zipError ? ' payment-input--error' : ''}`}
                  autoComplete="postal-code"
                  aria-required="true"
                  aria-invalid={zipError ? 'true' : 'false'}
                  placeholder="5 digits"
                />
                {zipError && <p className="form-field__error">{zipError}</p>}
              </div>
            </div>
          </>
        )}

        {submitError && <p className="form-field__error" style={{ marginTop: 16 }}>{submitError}</p>}

        <button
          type="submit"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          className="primary-cta payment-finish"
        >
          <span className="payment-finish__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M5 12 L10 17 L19 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {loading ? 'Saving...' : 'Finish'}
        </button>

        <p className="payment-payout-note">
          Payment usually arrives within 24&ndash;48 hours after approval.
        </p>
      </form>
    </StepLayout>
  );
}
